import React from "react";
import styled, { css, keyframes } from "styled-components";
import BrandLogo from "./brand-logo";

type LoaderProps = {
  exiting?: boolean;
  label?: string;
};

/** Full-screen splash — initial app load only */
const Loader = ({
  exiting = false,
  label = "Preparing your experience",
}: LoaderProps) => {
  return (
    <LoaderScreen
      $exiting={exiting}
      aria-live="polite"
      aria-busy={!exiting}
    >
      <AmbientLayer aria-hidden />

      <GlassCard $exiting={exiting}>
        <AnimatedStack>
          <SplashLogo aria-hidden>
            <BrandLogo size={88} />
          </SplashLogo>

          <BrandRow>
            <BrandFirst>Mark</BrandFirst>
            <BrandLast>Solano</BrandLast>
          </BrandRow>

          <RoleLine>Senior Software Engineer</RoleLine>

          <OrbitLoader aria-hidden>
            <OrbitRing />
            <OrbitCore />
          </OrbitLoader>

          <StatusLine>
            <StatusText>{label}</StatusText>
            <StatusDots aria-hidden>
              <span />
              <span />
              <span />
            </StatusDots>
          </StatusLine>

          <ProgressLine aria-hidden>
            <ProgressGlow />
          </ProgressLine>
        </AnimatedStack>
      </GlassCard>
    </LoaderScreen>
  );
};

/** Minimal spinner for in-app route changes only */
export const RouteLoader = () => (
  <NavLoaderWrap role="status" aria-label="Loading page">
    <NavSpinner aria-hidden>
      <OrbitRing />
    </NavSpinner>
  </NavLoaderWrap>
);

export default Loader;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const exitScreen = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(1.015);
  }
`;

const drift = keyframes`
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(4%, -3%) scale(1.05);
  }
`;

const orbit = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.35;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(220%);
  }
`;

const dotWave = keyframes`
  0%, 80%, 100% {
    opacity: 0.25;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
`;

const NavLoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: min(50vh, 420px);
  padding: 3rem 1rem;
`;

const NavSpinner = styled.div`
  position: relative;
  width: 2rem;
  height: 2rem;
`;

const LoaderScreen = styled.div<{ $exiting: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  background: radial-gradient(
    ellipse at 50% 0%,
    var(--loader-bg-start) 0%,
    var(--loader-bg-mid) 45%,
    var(--loader-bg-end) 100%
  );
  color: var(--white-dart);

  ${({ $exiting }) =>
    $exiting &&
    css`
      animation: ${exitScreen} 0.55s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    `}
`;

const AmbientLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    filter: blur(72px);
    animation: ${drift} 9s ease-in-out infinite;
  }

  &::before {
    width: min(42vw, 320px);
    height: min(42vw, 320px);
    top: 12%;
    left: 18%;
    background: var(--loader-glow-a);
  }

  &::after {
    width: min(36vw, 280px);
    height: min(36vw, 280px);
    bottom: 8%;
    right: 14%;
    background: var(--loader-glow-b);
    animation-delay: -4.5s;
  }
`;

const GlassCard = styled.div<{ $exiting: boolean }>`
  position: relative;
  z-index: 1;
  min-width: min(88vw, 320px);
  border-radius: 16px;
  border: 1px solid var(--border-subtle);
  background: linear-gradient(
    145deg,
    var(--loader-glass-start) 0%,
    var(--loader-glass-end) 100%
  );
  backdrop-filter: blur(18px);
  box-shadow:
    0 24px 64px var(--shadow-medium),
    inset 0 1px 0 var(--border-subtle);
`;

const AnimatedStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 2.5rem 2.25rem;

  & > * {
    animation: ${fadeInUp} 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  & > *:nth-child(1) {
    animation-delay: 0.04s;
  }
  & > *:nth-child(2) {
    animation-delay: 0.1s;
  }
  & > *:nth-child(3) {
    animation-delay: 0.16s;
  }
  & > *:nth-child(4) {
    animation-delay: 0.22s;
  }
  & > *:nth-child(5) {
    animation-delay: 0.28s;
  }
  & > *:nth-child(6) {
    animation-delay: 0.34s;
  }
`;

const SplashLogo = styled.div`
  line-height: 0;
`;

const BrandRow = styled.div`
  display: inline-flex;
  align-items: stretch;
  font-size: 1.2rem;
  letter-spacing: 0.03em;
  border-radius: 6px;
  overflow: hidden;
`;

const BrandFirst = styled.span`
  font-weight: 600;
  background: var(--white-pure);
  color: var(--navy);
  padding: 0.3em 0.62em;
`;

const BrandLast = styled.span`
  font-weight: 500;
  color: var(--white-dart);
  padding: 0.3em 0.62em;
  background: var(--accent-bg-hover);
`;

const RoleLine = styled.p`
  margin: -0.15rem 0 0;
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--white-dart);
  opacity: 0.55;
`;

const OrbitLoader = styled.div`
  position: relative;
  width: 2.85rem;
  height: 2.85rem;
  margin: 0.35rem 0;
`;

const OrbitRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg 300deg,
    var(--green) 300deg 360deg
  );
  mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px));
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px));
  animation: ${orbit} 1.1s cubic-bezier(0.5, 0, 0.35, 1) infinite;
`;

const OrbitCore = styled.div`
  position: absolute;
  inset: 32%;
  border-radius: 50%;
  background: var(--accent-bg-hover);
  animation: ${pulse} 1.8s ease-in-out infinite;
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const StatusText = styled.span`
  font-size: 0.82rem;
  font-weight: 400;
  letter-spacing: 0.04em;
  color: var(--white-dart);
  opacity: 0.75;
  font-family: "SF Mono", "Fira Code", "Roboto Mono", monospace;
`;

const StatusDots = styled.span`
  display: inline-flex;
  align-items: flex-end;
  gap: 3px;
  height: 1em;

  span {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: var(--green);
    animation: ${dotWave} 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }
    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
`;

const ProgressLine = styled.div`
  width: 100%;
  max-width: 200px;
  height: 1px;
  margin-top: 0.25rem;
  border-radius: 1px;
  background: var(--border-muted);
  overflow: hidden;
  position: relative;
`;

const ProgressGlow = styled.div`
  position: absolute;
  inset: 0;
  width: 40%;
  background: linear-gradient(
    90deg,
    transparent,
    var(--green),
    transparent
  );
  animation: ${shimmer} 1.8s ease-in-out infinite;
`;
